import {
  buildStudioHumanizationDimensionDisplays,
  type StudioHumanizationSnapshot,
  type StudioImprovementHint,
} from "../../../lib/studio/improvementHints";

function toneForStatus(status: "pass" | "warn" | "fail" | "review" | "guardrail") {
  if (status === "pass") {
    return {
      background: "rgba(44, 93, 67, 0.14)",
      color: "#214735",
    };
  }

  if (status === "fail" || status === "guardrail") {
    return {
      background: "rgba(162, 62, 62, 0.12)",
      color: "#812d2d",
    };
  }

  return {
    background: "rgba(181, 141, 34, 0.16)",
    color: "#6d5210",
  };
}

function pill(label: string, status: "pass" | "warn" | "fail" | "review" | "guardrail") {
  const tone = toneForStatus(status);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "0.28rem 0.62rem",
        background: tone.background,
        color: tone.color,
        fontSize: "0.74rem",
        fontWeight: 700,
        textTransform: "capitalize",
      }}
    >
      {label}
    </span>
  );
}

function summaryCard(label: string, value: string, detail: string) {
  return (
    <div
      style={{
        borderRadius: 20,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background:
          "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "0.95rem",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.38rem",
          color: "#26221d",
          fontWeight: 700,
          fontSize: "1.05rem",
        }}
      >
        {value}
      </div>
      <p style={{ margin: "0.42rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
        {detail}
      </p>
    </div>
  );
}

export default function HumanizationPanel(props: {
  snapshot: StudioHumanizationSnapshot;
  recommendations: StudioImprovementHint[];
}) {
  const dimensions = buildStudioHumanizationDimensionDisplays(props.snapshot);

  return (
    <section style={{ marginTop: "1.15rem" }}>
      <div
        style={{
          fontSize: "0.74rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#7b726a",
          fontWeight: 700,
        }}
      >
        Humanization QA
      </div>
      <h4 style={{ margin: "0.45rem 0 0", fontSize: "1.28rem", color: "#26221d" }}>
        Humanization scorecard
      </h4>
      <p style={{ margin: "0.6rem 0 0", color: "#4d453d", lineHeight: 1.65 }}>
        Review the latest preview render against the A20 score dimensions before
        deciding whether to tune the draft.
      </p>

      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
        {pill(
          `${props.snapshot.humanization.total.score}/${props.snapshot.humanization.total.maxScore}`,
          props.snapshot.humanization.total.status
        )}
        {pill(props.snapshot.humanization.total.status, props.snapshot.humanization.total.status)}
        {pill(props.snapshot.channel, "review")}
      </div>

      <div
        style={{
          marginTop: "0.95rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "0.85rem",
        }}
      >
        {summaryCard("Source", props.snapshot.sourceLabel, props.snapshot.label)}
        {summaryCard("Request ID", props.snapshot.requestId, "Safe preview request identifier only.")}
        {summaryCard("Persona", props.snapshot.personaLabel, "Preview-only voice persona selection.")}
      </div>

      <div
        style={{
          marginTop: "0.95rem",
          borderRadius: 20,
          border: "1px solid rgba(41, 84, 103, 0.14)",
          background:
            "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
          padding: "1rem",
        }}
      >
        <div
          style={{
            fontSize: "0.72rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#5b7884",
            fontWeight: 700,
          }}
        >
          Preview excerpt
        </div>
        <p style={{ margin: "0.55rem 0 0", color: "#39535f", lineHeight: 1.7 }}>
          {props.snapshot.transcriptExcerpt}
        </p>
      </div>

      <div
        style={{
          marginTop: "0.95rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "0.85rem",
        }}
      >
        {dimensions.map((dimension) => (
          <section
            key={dimension.key}
            style={{
              borderRadius: 20,
              border: "1px solid rgba(54, 44, 35, 0.08)",
              background:
                "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
              padding: "0.95rem",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "0.7rem",
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontWeight: 700, color: "#26221d" }}>{dimension.label}</div>
              {pill(dimension.status, dimension.status)}
            </div>
            <div
              style={{
                marginTop: "0.45rem",
                color: "#173847",
                fontWeight: 700,
                fontSize: "1.05rem",
              }}
            >
              {dimension.score}/{dimension.maxScore}
            </div>
            <p style={{ margin: "0.45rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
              {dimension.detail}
            </p>
          </section>
        ))}
      </div>

      <section style={{ marginTop: "1.15rem" }}>
        <div
          style={{
            fontSize: "0.74rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#7b726a",
            fontWeight: 700,
          }}
        >
          Tuning recommendations
        </div>
        <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.85rem" }}>
          {props.recommendations.map((recommendation) => (
            <section
              key={recommendation.id}
              style={{
                borderRadius: 22,
                border: "1px solid rgba(54, 44, 35, 0.08)",
                background:
                  "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
                padding: "1rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.8rem",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.72rem",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "#85796e",
                      fontWeight: 700,
                    }}
                  >
                    Operator tuning
                  </div>
                  <h5 style={{ margin: "0.4rem 0 0", fontSize: "1.02rem", color: "#26221d" }}>
                    {recommendation.title}
                  </h5>
                </div>
                <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
                  {pill(recommendation.severity, recommendation.severity)}
                  {pill(recommendation.ownerControlLabel, "review")}
                </div>
              </div>
              <p style={{ margin: "0.6rem 0 0", color: "#4d453d", lineHeight: 1.65 }}>
                {recommendation.summary}
              </p>
              <div
                style={{
                  marginTop: "0.75rem",
                  borderRadius: 18,
                  background: "#f8f4ee",
                  border: "1px solid rgba(54, 44, 35, 0.08)",
                  padding: "0.85rem",
                  color: "#26221d",
                  lineHeight: 1.6,
                }}
              >
                {recommendation.recommendedAdjustment}
              </div>
              <p style={{ margin: "0.72rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
                {recommendation.rationale}
              </p>
              <div
                style={{
                  marginTop: "0.75rem",
                  display: "flex",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {recommendation.affectedDimensions.map((dimension) => (
                  <span
                    key={`${recommendation.id}_${dimension}`}
                    style={{
                      borderRadius: 999,
                      padding: "0.25rem 0.55rem",
                      background: "rgba(41, 84, 103, 0.08)",
                      color: "#295467",
                      fontSize: "0.76rem",
                      fontWeight: 700,
                    }}
                  >
                    {dimension}
                  </span>
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>
    </section>
  );
}
